import { Box, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import {
  Bar,
  BarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CardShell } from '../../atlas/cards/CardShell';
import { MetricRow } from '../../atlas/cards/MetricRow';
import { countYAxisReferenceGridLines } from '../../countYAxisReferenceGridLines';
import { CHART_GOAL_LINE_COLOR, CHART_GOAL_LINE_PROPS } from '../../constants';
import { roundedStackBarTopShape, stackBarTopShape, type BarShapeProps } from '../../rechartsStackedBarShapes';
import { SKYWAY_LONG_TERM_GOALS } from '../../safeT/benchmarks';
import {
  aggregateQuarterlyChartToAnnual,
  formatYoYSublabel,
  latest,
  parseQuarter,
  pctChange,
  buildCountYAxisScaleForChart,
} from '../componentProjectMetricUtils';
import {
  hasSafeTClientAcquisitionSubawardeeBreakdown,
  type SafeTComponentProjectData,
  type SafeTComponentProjectPoint,
} from './mapSafeTComponentProjectData';

type SafeTClientAcquisitionComponentCardProps = {
  data: SafeTComponentProjectData;
};

const CLIENT_ACQUISITION_SUBAWARDEE_COLORS = [
  '#7048E8',
  '#FF8787',
  '#FFA94D',
  '#20C997',
  '#F06595',
];
const CLIENTS_ACQUIRED_COLOR = '#FF922B';

function sumPriorYearYtd(
  series: SafeTComponentProjectPoint[],
  currentMeta: { year: number; quarter: number } | null,
  getValue: (point: SafeTComponentProjectPoint) => number
): number {
  if (!currentMeta) return 0;
  return series
    .filter((point) => {
      const meta = parseQuarter(point.quarter);
      return meta?.year === currentMeta.year - 1 && meta.quarter <= currentMeta.quarter;
    })
    .reduce((sum, point) => sum + getValue(point), 0);
}

function getActiveClientAcquisitionStackKeys(data: SafeTComponentProjectData): string[] {
  if (!hasSafeTClientAcquisitionSubawardeeBreakdown(data)) {
    return [];
  }

  const active = new Set<string>();
  for (const point of data.series) {
    for (const [subawardeeId, value] of Object.entries(point.clientsAcquiredBySubawardee ?? {})) {
      if (value > 0) active.add(subawardeeId);
    }
  }

  if (active.size > 0) {
    return data.clientAcquisitionSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
  }

  return data.clientAcquisitionSubawardeeIds;
}

export function SafeTClientAcquisitionComponentCard({ data }: SafeTClientAcquisitionComponentCardProps) {
  const theme = useMantineTheme();
  const series = data.series;
  const cur = latest(series);
  const subtitle = cur?.quarter ?? 'No data';
  const currentMeta = parseQuarter(cur?.quarter ?? '');
  const isStackedBySubawardee = hasSafeTClientAcquisitionSubawardeeBreakdown(data);
  const stackKeys = getActiveClientAcquisitionStackKeys(data);

  const ytdEngaged = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.clientsEngaged, 0)
    : null;
  const ytdAcquired = currentMeta
    ? series
        .filter((point) => {
          const meta = parseQuarter(point.quarter);
          return meta?.year === currentMeta.year && meta.quarter <= currentMeta.quarter;
        })
        .reduce((sum, point) => sum + point.clientsAcquired, 0)
    : null;

  const priorYearYtdEngaged = sumPriorYearYtd(series, currentMeta, (point) => point.clientsEngaged);
  const priorYearYtdAcquired = sumPriorYearYtd(series, currentMeta, (point) => point.clientsAcquired);

  const engagedYoyPct =
    ytdEngaged !== null && priorYearYtdEngaged !== 0
      ? pctChange(ytdEngaged, priorYearYtdEngaged)
      : null;
  const acquiredYoyPct =
    ytdAcquired !== null && priorYearYtdAcquired !== 0
      ? pctChange(ytdAcquired, priorYearYtdAcquired)
      : null;

  const curYtdConversion =
    ytdEngaged !== null && ytdAcquired !== null && ytdEngaged > 0
      ? (ytdAcquired / ytdEngaged) * 100
      : null;
  const prevYtdConversion =
    priorYearYtdEngaged > 0 ? (priorYearYtdAcquired / priorYearYtdEngaged) * 100 : null;
  const conversionYoyPct =
    curYtdConversion !== null && prevYtdConversion !== null && prevYtdConversion !== 0
      ? ((curYtdConversion - prevYtdConversion) / prevYtdConversion) * 100
      : null;

  const engagedAnnualGoal = cur?.goalTargets?.clientsEngaged;
  const acquiredAnnualGoal = cur?.goalTargets?.clientsAcquired;

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  const acquiredChartGoal = acquiredAnnualGoal ?? SKYWAY_LONG_TERM_GOALS.acquiredPerYear;

  const chartData = isStackedBySubawardee
    ? (() => {
        const byYear = new Map<number, Record<string, number | string>>();

        for (const point of series) {
          const meta = parseQuarter(point.quarter);
          if (!meta) continue;

          const row = byYear.get(meta.year) ?? {
            year: meta.year,
            yearLabel: String(meta.year),
          };

          for (const subawardeeId of stackKeys) {
            row[subawardeeId] =
              Number(row[subawardeeId] ?? 0) + (point.clientsAcquiredBySubawardee?.[subawardeeId] ?? 0);
          }

          byYear.set(meta.year, row);
        }

        return Array.from(byYear.values()).sort((a, b) => Number(a.year) - Number(b.year));
      })()
    : aggregateQuarterlyChartToAnnual(
        series.map((point) => ({
          quarter: point.quarter,
          clientsAcquired: point.clientsAcquired,
        })),
        ['clientsAcquired']
      );

  const chartMax = isStackedBySubawardee
    ? Math.max(
        0,
        ...chartData.flatMap((row) =>
          stackKeys.map((subawardeeId) => Number(row[subawardeeId] ?? 0))
        )
      )
    : Math.max(0, ...chartData.map((point) => Number(point.clientsAcquired)));

  const chartGoalValues = [acquiredChartGoal];
  const { yAxisMax, yAxisTicks } = buildCountYAxisScaleForChart([chartMax], chartGoalValues);
  const gridLineStroke =
    theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.12)';

  return (
    <CardShell title="Client Acquisition" subtitle={subtitle}>
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'Engaged (YTD)',
              labelTooltip: 'Calendar year to date potential clients engaged.',
              value: (
                <Text size="lg" weight={800}>
                  {ytdEngaged ?? '—'}
                </Text>
              ),
              sublabel: (
                <>
                  {formatYoYSublabel(engagedYoyPct)}
                  {engagedAnnualGoal !== undefined ? (
                    <>
                      <br />
                      {`Goal ${engagedAnnualGoal}/year`}
                    </>
                  ) : null}
                </>
              ),
            },
            {
              label: 'Acquired (YTD)',
              labelTooltip: 'Calendar year to date clients acquired.',
              value: (
                <Text size="lg" weight={800}>
                  {ytdAcquired ?? '—'}
                </Text>
              ),
              sublabel: (
                <>
                  {formatYoYSublabel(acquiredYoyPct)}
                  {acquiredAnnualGoal !== undefined ? (
                    <>
                      <br />
                      {`Goal ${acquiredAnnualGoal}/year`}
                    </>
                  ) : null}
                </>
              ),
            },
            {
              label: 'Conversion (YTD)',
              labelTooltip: 'Percentage of calendar year to date engaged clients that were acquired.',
              value: (
                <Text size="lg" weight={800}>
                  {curYtdConversion !== null ? `${Math.round(curYtdConversion)}%` : '—'}
                </Text>
              ),
              sublabel: formatYoYSublabel(conversionYoyPct),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2, marginTop: '0.75rem' }}>
          {isStackedBySubawardee ? 'Annual Clients Acquired by Sub-awardee' : 'Annual Clients Acquired'}
        </Text>

        <Box sx={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={4} barCategoryGap="12%">
              <XAxis
                dataKey="yearLabel"
                tick={{ fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
                minTickGap={12}
              />
              <YAxis
                orientation="right"
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                width={44}
                tickMargin={8}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
              />
              {countYAxisReferenceGridLines(yAxisTicks, gridLineStroke, chartGoalValues)}
              <ReferenceLine
                y={acquiredChartGoal}
                stroke={CHART_GOAL_LINE_COLOR}
                strokeWidth={CHART_GOAL_LINE_PROPS.strokeWidth}
                strokeDasharray={CHART_GOAL_LINE_PROPS.strokeDasharray}
                ifOverflow="extendDomain"
              />
              <Tooltip
                cursor={false}
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;

                  if (isStackedBySubawardee) {
                    const acquiredTotal = stackKeys.reduce((sum, subawardeeId) => {
                      const entry = payload.find((item) => item.dataKey === subawardeeId);
                      return sum + Number(entry?.value ?? 0);
                    }, 0);

                    return (
                      <div style={tooltipShellStyle}>
                        <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                          {label}
                        </div>
                        {stackKeys.map((subawardeeId) => {
                          const entry = payload.find((item) => item.dataKey === subawardeeId);
                          const value = Number(entry?.value ?? 0);
                          if (value <= 0) return null;
                          const unit = value === 1 ? 'Acquisition' : 'Acquisitions';
                          return (
                            <div
                              key={subawardeeId}
                              style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}
                            >
                              {subawardeeId}: {value} {unit}
                            </div>
                          );
                        })}
                        <div style={{ fontWeight: 700, fontSize: 13, marginTop: 6 }}>
                          Total: {acquiredTotal}{' '}
                          {acquiredTotal === 1 ? 'Acquisition' : 'Acquisitions'}
                        </div>
                      </div>
                    );
                  }

                  const acquired = Number(
                    payload.find((entry) => entry.dataKey === 'clientsAcquired')?.value ?? 0
                  );

                  return (
                    <div style={tooltipShellStyle}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>
                        {label}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>
                        {acquired} {acquired === 1 ? 'Acquisition' : 'Acquisitions'}
                      </div>
                    </div>
                  );
                }}
              />
              {isStackedBySubawardee ? (
                stackKeys.map((subawardeeId, index) => (
                  <Bar
                    key={subawardeeId}
                    dataKey={subawardeeId}
                    name={subawardeeId}
                    stackId="acquired"
                    fill={CLIENT_ACQUISITION_SUBAWARDEE_COLORS[index % CLIENT_ACQUISITION_SUBAWARDEE_COLORS.length]}
                    shape={stackBarTopShape(stackKeys, subawardeeId)}
                    barSize={18}
                    isAnimationActive={false}
                    activeBar={false}
                  />
                ))
              ) : (
                <Bar
                  dataKey="clientsAcquired"
                  name="Clients Acquired"
                  fill={CLIENTS_ACQUIRED_COLOR}
                  shape={(props: BarShapeProps) => roundedStackBarTopShape(props, true)}
                  barSize={18}
                  isAnimationActive={false}
                  activeBar={false}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {isStackedBySubawardee ? (
          <Group position="center" spacing="lg" mt={4}>
            {stackKeys.map((subawardeeId, index) => (
              <Group key={subawardeeId} spacing={6} noWrap>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      CLIENT_ACQUISITION_SUBAWARDEE_COLORS[
                        index % CLIENT_ACQUISITION_SUBAWARDEE_COLORS.length
                      ],
                    flexShrink: 0,
                  }}
                />
                <Text size="xs" color="dimmed">
                  {subawardeeId}
                </Text>
              </Group>
            ))}
          </Group>
        ) : null}
      </Stack>
    </CardShell>
  );
}
